import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AvatarUploader({ size = 96 }) {
  const { user, token, refresh } = useAuth();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) {
      toast.error("Image must be under 3 MB.");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      await axios.post(`${API}/me/avatar`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Profile picture updated.");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const src = user?.avatar_url ? `${BACKEND}${user.avatar_url}` : "";

  return (
    <div
      className="relative shrink-0 rounded-full overflow-hidden bg-muted ring-1 ring-secondary/40 group cursor-pointer"
      style={{ width: size, height: size }}
      onClick={() => fileRef.current?.click()}
      data-testid="avatar-uploader"
    >
      {src ? (
        <img src={src} alt="Profile" className="w-full h-full object-cover" data-testid="avatar-img" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <User className="text-muted-foreground" size={size * 0.45} />
        </div>
      )}
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <Camera size={size * 0.28} className="text-secondary" />
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={onPick}
        disabled={busy}
        data-testid="avatar-file-input"
      />
    </div>
  );
}
